import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * Rewards panel for the dashboard tab — replaces the old standalone
 * LoyaltyScreen when embedded under the dashboard. Uses the signed-in user's
 * real name, drops the duplicate internal nav bar, fits the embedded panel
 * layout. Standalone /unified/loyalty still uses this same component (just
 * without the dashboard frame).
 */

const EASE = [0.22, 1, 0.36, 1];

// Demo data until a loyalty backend exists — mirrors the archive shape so the
// UI is visually complete for design review. Points/tier/history will come
// from the backend once loyalty endpoints ship.
const DEMO_POINTS = 1847;
const DEMO_TIER = 'Plus';
const NEXT_MILESTONE = { name: 'Concierge reservations', pts: 2500 };

const DEMO_HISTORY = [
  { kind: 'earn',   icon: 'restaurant', title: "Il Violino",                   sub: 'Dinner · Apr 17',      pts: 62,   amt: '$62.00' },
  { kind: 'earn',   icon: 'restaurant', title: 'Koji Ramen Bar',               sub: 'Lunch · Apr 12',       pts: 24,   amt: '$23.50' },
  { kind: 'redeem', icon: 'gift',       title: 'Redeemed: $10 off',            sub: 'Apr 8 · Koji',         pts: -100, amt: '' },
  { kind: 'earn',   icon: 'shield',     title: 'Verified safe allergen report', sub: 'Sage & Stem · Apr 4', pts: 15,   amt: 'Bonus' },
  { kind: 'earn',   icon: 'restaurant', title: 'Maya Mezze',                   sub: 'Dinner · Apr 3',       pts: 48,   amt: '$47.80' },
];

const REWARDS = [
  { cost: 100,  title: '$10 off your next order',                       section: 'Cash back', tone: 'green',  icon: 'gift' },
  { cost: 250,  title: 'Unlock 5 extra restaurant cards past your limit', section: 'Discovery', tone: 'blue',   icon: 'heart' },
  { cost: 350,  title: 'Priority allergen verification',                section: 'Discovery', tone: 'red',    icon: 'shield' },
  { cost: 500,  title: 'Priority reservation — Fri/Sat prime time',      section: 'Access',   tone: 'purple', icon: 'calendar' },
  { cost: 750,  title: 'Event ticket: allergen-safe tasting',            section: 'Events',   tone: 'orange', icon: 'star' },
  { cost: 1500, title: 'Free delivery for 90 days',                      section: 'Cash back',tone: 'green',  icon: 'truck' },
];

const TONE_COLORS = {
  blue:   { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
  green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
  red:    { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: 'var(--cwm-skip)' },
  purple: { bg: '#F1E8FE',                fg: '#5B21B6',               solid: '#7C3AED' },
  orange: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
};

export default function UnifiedRewards({ userData = null, embedded = true }) {
  // Pull real loyalty state from the backend. Seeds with the demo
  // values so the screen renders immediately; the real numbers replace
  // them as soon as the fetch resolves. If the fetch fails (offline,
  // unauthenticated) we stay on demo — the UI is never blank.
  const userId = userData?.id || Number(localStorage.getItem('userId')) || null;
  const [overview, setOverview] = useState({
    points: DEMO_POINTS,
    tier: DEMO_TIER,
    next_milestone: NEXT_MILESTONE,
    history: DEMO_HISTORY,
  });
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const live = await dataService.getRewardsOverview(userId);
        if (cancelled || !live) return;
        setOverview({
          points: live.points ?? 0,
          tier: live.tier || DEMO_TIER,
          next_milestone: live.next_milestone || NEXT_MILESTONE,
          // Backend ledger rows and DEMO_HISTORY both have {kind, icon,
          // title, sub, pts/points, amt} — they render the same. Use live
          // history when present; otherwise keep the demo so the section
          // isn't empty for new users with zero activity.
          history: (live.history && live.history.length > 0)
            ? live.history.map((h) => ({
                ...h,
                pts: h.points ?? h.pts,
              }))
            : DEMO_HISTORY,
        });
      } catch {
        // Keep demo seeds on error — no visible breakage.
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const points = overview.points;
  const tier = overview.tier;
  const milestone = overview.next_milestone || NEXT_MILESTONE;
  const pct = Math.min(100, Math.round((points / Math.max(1, milestone.pts)) * 100));
  const firstName = (userData?.name || '').split(' ')[0] || 'friend';
  const toNext = Math.max(0, milestone.pts - points);
  const history = overview.history;

  const redeem = async (reward) => {
    if (!userId) {
      setToast({ tone: 'red', text: 'Sign in to redeem rewards.' });
      return;
    }
    if (points < reward.cost) {
      setToast({ tone: 'red', text: `You need ${reward.cost - points} more points.` });
      return;
    }
    try {
      const updated = await dataService.redeemReward(userId, {
        cost: reward.cost,
        title: reward.title,
        icon: reward.icon,
      });
      setOverview({
        points: updated.points,
        tier: updated.tier,
        next_milestone: updated.next_milestone || milestone,
        history: (updated.history || []).map((h) => ({ ...h, pts: h.points ?? h.pts })),
      });
      setToast({ tone: 'green', text: `Redeemed: ${reward.title}` });
    } catch (e) {
      setToast({ tone: 'red', text: e.message === 'Insufficient points' ? 'Not enough points.' : 'Could not redeem right now.' });
    }
    setTimeout(() => setToast(null), 2600);
  };

  return (
    <div style={{ padding: embedded ? '18px 20px' : '28px 20px 80px', maxWidth: 1080, margin: '0 auto' }}>

      {/* Intro */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, ease: EASE }}
        style={{ marginBottom: 14 }}
      >
        <div className="cwm-eyebrow">WELCOME BACK, {firstName.toUpperCase()}</div>
      </motion.div>

      {toast && (
        <div style={{
          padding: '10px 14px', borderRadius: 12, marginBottom: 14,
          background: toast.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
          color:      toast.tone === 'red' ? 'var(--cwm-red-text)'  : 'var(--cwm-green-text)',
          fontSize: 13, fontWeight: 800,
        }}>
          {toast.text}
        </div>
      )}

      {/* Top row: points hero + milestone */}
      <div className="cwm-rewards-top" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14, marginBottom: 22 }}>
        <PointsHero points={points} tier={tier} />
        <MilestoneCard points={points} pct={pct} toNext={toNext} milestoneName={milestone.name} milestonePts={milestone.pts} />
      </div>

      {/* Redeem catalog */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.14, ease: EASE }}
        style={{ marginBottom: 22 }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.01em' }}>
            Redeem your points
          </h2>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>{REWARDS.length} rewards</span>
        </div>
        <div className="cwm-rewards-catalog" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {REWARDS.map((r, i) => <RewardCard key={r.title} reward={r} userPoints={points} index={i} onRedeem={() => redeem(r)} />)}
        </div>
      </motion.div>

      {/* Activity */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.2, ease: EASE }}
        className="cwm-sheet"
        style={{ padding: 0, overflow: 'hidden' }}
      >
        <header style={{ padding: '16px 20px', borderBottom: '1px solid var(--cwm-gray-100)' }}>
          <div className="cwm-eyebrow">RECENT ACTIVITY</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2 }}>
            Earned & redeemed
          </div>
        </header>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {history.map((h, i) => (
            <li key={h.id ?? i} style={{
              padding: '12px 20px',
              borderBottom: i < history.length - 1 ? '1px solid var(--cwm-gray-100)' : 'none',
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 12,
                display: 'grid', placeItems: 'center', flexShrink: 0,
                background: h.kind === 'redeem' ? 'var(--cwm-red-light)' : 'var(--cwm-blue-pale)',
                color: h.kind === 'redeem' ? 'var(--cwm-red-text)' : 'var(--cwm-blue-deep)',
              }}>
                <HistoryIcon name={h.icon} size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>{h.title}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>{h.sub}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontSize: 14, fontWeight: 900,
                  color: h.kind === 'redeem' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
                }}>
                  {h.pts > 0 ? '+' : ''}{h.pts} pts
                </div>
                {h.amt && (
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>{h.amt}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </motion.div>

      <style>{`
        @media (min-width: 720px) {
          .cwm-rewards-top { grid-template-columns: 1.15fr 0.85fr !important; }
        }
      `}</style>
    </div>
  );
}

// ---------- sub-components ----------

function PointsHero({ points, tier }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.46, delay: 0.08, ease: EASE }}
      style={{
        position: 'relative', overflow: 'hidden',
        borderRadius: 24, padding: 24,
        background: 'linear-gradient(160deg, #1E9BFF 0%, #0F7FE8 60%, #023E8A 100%)',
        color: '#fff',
        boxShadow: '0 24px 48px rgba(15,127,232,0.32), inset 0 1px 0 rgba(255,255,255,0.2)',
      }}
    >
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 1.5px, transparent 1.6px)',
        backgroundSize: '22px 22px', opacity: 0.28,
      }}/>
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.78 }}>
            POINTS BALANCE
          </div>
          <span style={{
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(255,255,255,0.22)', color: '#fff',
            fontSize: 11, fontWeight: 900, letterSpacing: '0.08em',
            backdropFilter: 'blur(4px)',
          }}>
            CWM {tier.toUpperCase()}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, lineHeight: 1 }}>
          <div style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-0.03em' }}>
            {points.toLocaleString()}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, opacity: 0.8 }}>pts</div>
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 600, opacity: 0.94 }}>
          ≈ <strong>${Math.floor(points / 10)}</strong> toward your next order
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button style={{
            padding: '10px 18px', borderRadius: 999,
            background: '#fff', color: 'var(--cwm-blue-deep)',
            border: 'none', cursor: 'pointer',
            fontFamily: 'var(--cwm-font)', fontSize: 13.5, fontWeight: 900,
            boxShadow: '0 6px 16px rgba(10,37,64,0.2)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <GiftIcon size={13} /> Redeem points
          </button>
          <button style={{
            padding: '10px 18px', borderRadius: 999,
            background: 'rgba(255,255,255,0.16)', color: '#fff',
            border: '1.5px solid rgba(255,255,255,0.32)', cursor: 'pointer',
            fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
            backdropFilter: 'blur(6px)',
          }}>
            How points work
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function MilestoneCard({ points, pct, toNext, milestoneName, milestonePts }) {
  // Fallbacks keep the card working even when called without the
  // per-tier props (legacy render paths, static demos).
  const name = milestoneName || NEXT_MILESTONE.name;
  const target = milestonePts ?? NEXT_MILESTONE.pts;
  return (
    <motion.div
      initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.46, delay: 0.12, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)' }}>NEXT MILESTONE</div>
        <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.01em', marginTop: 4 }}>
          {name}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--cwm-gray-700)', fontWeight: 600, marginTop: 4 }}>
          <strong style={{ color: 'var(--cwm-blue-deep)' }}>{toNext.toLocaleString()} pts</strong> to go — earn them dining out.
        </div>
      </div>

      <div>
        <div style={{ height: 10, borderRadius: 999, background: 'var(--cwm-gray-100)', overflow: 'hidden', marginBottom: 6 }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            style={{ height: '100%', background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)', borderRadius: 999 }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, fontWeight: 800, color: 'var(--cwm-gray-500)' }}>
          <span>{points.toLocaleString()} pts</span>
          <span>{target.toLocaleString()} pts</span>
        </div>
      </div>

      <div style={{
        padding: '10px 14px', borderRadius: 14,
        background: 'var(--cwm-green-light)',
        border: '1px solid #A6E8C2',
        color: 'var(--cwm-green-text)',
        fontSize: 12.5, fontWeight: 800,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 16 }}>✦</span>
        <span style={{ lineHeight: 1.4 }}>Weekend bonus — earn <strong>1.5×</strong> at partner restaurants through Sunday.</span>
      </div>
    </motion.div>
  );
}

function RewardCard({ reward, userPoints, index, onRedeem }) {
  const tone = TONE_COLORS[reward.tone] || TONE_COLORS.blue;
  const canRedeem = userPoints >= reward.cost;
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, delay: 0.18 + index * 0.04, ease: EASE }}
      whileHover={{ y: -2 }}
      className="cwm-sheet"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          display: 'grid', placeItems: 'center',
          background: `linear-gradient(160deg, #fff, ${tone.bg})`,
          border: `1.5px solid ${tone.solid}33`,
          color: tone.solid,
          flexShrink: 0,
        }}>
          <RewardIcon name={reward.icon} size={18} />
        </div>
        <span style={{
          padding: '4px 9px', borderRadius: 999,
          background: tone.bg, color: tone.fg,
          fontSize: 10, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          {reward.section}
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)', lineHeight: 1.35, flex: 1 }}>
        {reward.title}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)' }}>
          {reward.cost.toLocaleString()} <span style={{ fontSize: 11, color: 'var(--cwm-gray-500)', fontWeight: 800 }}>pts</span>
        </div>
        <button
          disabled={!canRedeem}
          onClick={() => canRedeem && onRedeem && onRedeem(reward)}
          style={{
            padding: '6px 14px', borderRadius: 999,
            background: canRedeem ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : 'var(--cwm-gray-100)',
            color: canRedeem ? '#fff' : 'var(--cwm-gray-500)',
            border: 'none', cursor: canRedeem ? 'pointer' : 'not-allowed',
            fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 900,
            boxShadow: canRedeem ? '0 4px 10px rgba(15,127,232,0.28)' : 'none',
          }}>
          {canRedeem ? 'Redeem' : `${(reward.cost - userPoints).toLocaleString()} to go`}
        </button>
      </div>
    </motion.article>
  );
}

// ---------- icons ----------
function GiftIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 110-5c3 0 4.5 5 4.5 5zM12 7h4.5a2.5 2.5 0 100-5c-3 0-4.5 5-4.5 5z"/></svg>; }
function HistoryIcon({ name, size = 16 }) {
  const paths = {
    restaurant: 'M7 2v6a3 3 0 006 0V2M17 2v9M17 11c0 3 2 6 2 6h-4s2-3 2-6M10 11v11M7 22h6',
    gift:       'M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 110-5c3 0 4.5 5 4.5 5z',
    shield:     'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.restaurant}/></svg>;
}
function RewardIcon({ name, size = 18 }) {
  const paths = {
    gift:     'M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 110-5c3 0 4.5 5 4.5 5z',
    heart:    'M12 21s-7-5.5-7-11a4 4 0 017-2.6A4 4 0 0119 10c0 5.5-7 11-7 11z',
    shield:   'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
    calendar: 'M4 5h16v16H4zM4 10h16M9 3v4M15 3v4',
    star:     'M12 2l3 7 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z',
    truck:    'M3 8h11v9H3zM14 11h4l3 3v3h-7M6 20a2 2 0 100-4 2 2 0 000 4zM18 20a2 2 0 100-4 2 2 0 000 4z',
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={name === 'heart' || name === 'star' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d={paths[name] || paths.gift}/>
    </svg>
  );
}
